import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import {
  clearMessages,
  clearSelectedUser,
  clearThinkingMessages,
  getBotsList,
  setSelectedBot,
} from '@/store/chat/actions';

import { selectBotsList, selectChat, selectSelectedBot } from '@/store/chat/selectors';

import { Avatar } from 'antd';
import { BotInstructionsModal, ModelSelector } from '@/components';
import { InfoCircleOutlined, InfoOutlined } from '@ant-design/icons';
import { FiChevronLeft } from 'react-icons/fi';

import ChatModal from './ChatModal';
import TokenUsageIndicator from './TokenUsageIndicator';

import { isDevelopmentMode } from '@/utils/domainValidation';
import useImage from '@shared/hooks/useImage';

import PropTypes from 'prop-types';

import styles from '../Messages.module.scss';

const Header = ({ socket }) => {
  const { selectedUser, selectedChatTopic } = useSelector(selectChat);
  const botsList = useSelector(selectBotsList);
  const selectedBot = useSelector(selectSelectedBot);
  const { t } = useTranslation('chat');

  const avatar = useImage(selectedChatTopic ? selectedChatTopic?.photo : selectedUser?.photo, 'avatar');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [visibleGroupModal, setVisibleGroupModal] = useState(false);
  const [visibleInstructionsModal, setVisibleInstructionsModal] = useState(false);
  const handleBack = () => {
    // Очищаем сообщения при закрытии чата
    dispatch(clearMessages());
    dispatch(clearSelectedUser());
  };

  const isBot =
    selectedUser?.bot?.botId ||
    selectedUser?.botId ||
    selectedUser?.isBot === true ||
    selectedUser?.userType === 'bot' ||
    selectedChatTopic?.bot?.botId ||
    selectedChatTopic?.isBot === true ||
    selectedChatTopic?.botId;

  console.log('🔍 Header Bot check:', {
    selectedUser,
    selectedChatTopic,
    botId: selectedUser?.botId,
    isBot,
  });

  // Effect to auto-select bot when chatting with a bot
  useEffect(() => {
    if (isBot && botsList.length > 0) {
      const botId = selectedUser?.bot?.botId || selectedUser?.botId;
      const currentBot = botsList.find(bot => bot.botId === botId);

      if (currentBot && selectedBot?.botId !== botId) {
        dispatch(setSelectedBot(currentBot));
      }
    }
  }, [isBot, selectedUser, botsList, selectedBot, dispatch]);

  // Load bots list if not loaded yet
  useEffect(() => {
    if (isBot && botsList.length === 0) {
      dispatch(getBotsList());
    }
  }, [isBot, botsList.length, dispatch]);

  const renderName = () => {
    if (selectedChatTopic) return selectedChatTopic?.chatName;
    if (isBot) return selectedUser?.bot?.botName || selectedUser?.botName;
    return selectedUser?.chatName;
  };

  const handleShowInstructions = e => {
    e.stopPropagation(); // Предотвращаем всплытие события
    setVisibleInstructionsModal(true);
  };

  const getBotData = () => {
    const bot = selectedUser?.bot || selectedUser;
    return {
      botName: bot?.botName,
      botDescription: bot?.botDescription,
      detailedInstructions: bot?.detailedInstructions,
    };
  };

  const renderGroupOrUser = () => {
    if (selectedUser?.chatName || selectedChatTopic?.chatName || selectedUser?.botName) {
      return (
        <div className={styles.header__link} onClick={() => setVisibleGroupModal(true)}>
          {!selectedChatTopic && !isBot && <Avatar size={36} src={avatar} />}
          <div className={styles['header__name-block']}>
            <div className={styles.header__nameContainer}>
              <p className={styles.header__name}>{renderName()}</p>
              {isBot && (
                <InfoCircleOutlined
                  style={{ fontSize: '16px', color: 'var(--personalColor)' }}
                  onClick={handleShowInstructions}
                ></InfoCircleOutlined>
              )}
            </div>
            {isBot && (
              <p className={styles.header__botDescription}>
                {selectedUser?.bot?.botDescription || selectedUser?.botDescription}
              </p>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <Link to={'/collegues/' + selectedUser?.id} className={styles.header__link}>
          {!isBot && <Avatar size={36} src={avatar} />}
          <p className={styles.header__name}>
            {selectedUser.firstName} {selectedUser.lastName}
          </p>
        </Link>
      );
    }
  };

  return (
    <div className={styles.header}>
      {renderGroupOrUser()}
      {isBot && <TokenUsageIndicator socket={socket} />}
      {isBot && isDevelopmentMode() && (
        <div className={styles.header__modelSelector}>
          <ModelSelector size='small' />
        </div>
      )}
      <div onClick={handleBack} className={styles.header__back}>
        <FiChevronLeft size={16} />
        <p>{t('back')}</p>
      </div>
      {selectedUser.trackId && (
        <div onClick={() => navigate(`/track/${selectedUser.trackId}`)} className={styles.header__track}>
          <p>{t('goToTrack')}</p>
        </div>
      )}
      {!!selectedUser?.chatName && !isBot && (
        <ChatModal
          isTopic={!!selectedChatTopic?.chatId}
          onClose={() => setVisibleGroupModal(false)}
          visible={visibleGroupModal}
        />
      )}

      {isBot && (
        <BotInstructionsModal
          open={visibleInstructionsModal}
          onCancel={() => setVisibleInstructionsModal(false)}
          botName={getBotData().botName}
          detailedInstructions={getBotData().detailedInstructions}
        />
      )}
    </div>
  );
};

Header.propTypes = {
  socket: PropTypes.object,
};

export default memo(Header);
